import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Loader2, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AgencyDemoModal = ({ open, onOpenChange, billingPreference = 'monthly' }) => {
  const { language } = useLanguage();
  const isEs = language === 'es';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    properties_count: 10,
    locations: '',
    billing_preference: billingPreference,
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { type: 'success'|'redirect'|'error', message, redirect_to? }

  const t = (es, en) => (isEs ? es : en);

  const handleChange = (field) => (e) => {
    const value = field === 'properties_count' ? Number(e.target.value) : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetAndClose = () => {
    setResult(null);
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      properties_count: 10,
      locations: '',
      billing_preference: billingPreference,
      message: '',
    });
    onOpenChange(false);
  };

  const scrollToPricing = () => {
    resetAndClose();
    setTimeout(() => {
      const el = document.getElementById('pricing');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);

    try {
      const res = await fetch(`${API_URL}/api/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, language, source: 'agency_pricing_cta' }),
      });

      const data = await res.json();

      if (res.status === 201) {
        setResult({
          type: 'success',
          message: isEs ? data.message_es : data.message_en,
        });
      } else if (res.status === 422 && data.detail?.error === 'properties_below_agency_minimum') {
        setResult({
          type: 'redirect',
          message: isEs ? data.detail.message_es : data.detail.message_en,
          redirect_to: data.detail.redirect_to,
        });
      } else if (res.status === 422 && Array.isArray(data.detail)) {
        // Pydantic validation error
        const first = data.detail[0];
        setResult({
          type: 'error',
          message: t(
            `Revisa los campos: ${first.loc?.[1] || 'formulario'}`,
            `Please check fields: ${first.loc?.[1] || 'form'}`
          ),
        });
      } else {
        setResult({
          type: 'error',
          message: t(
            'No pudimos enviar la solicitud. Intenta de nuevo o escribe a support@villapilot.ai.',
            'We could not submit your request. Please try again or email support@villapilot.ai.'
          ),
        });
      }
    } catch (err) {
      setResult({
        type: 'error',
        message: t(
          'Error de conexión. Verifica tu internet e intenta de nuevo.',
          'Network error. Please check your connection and retry.'
        ),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="agency-demo-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-playfair">
            {t('Solicita tu Demo Agency', 'Book your Agency Demo')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'Para portafolios de 10+ villas. Nuestro equipo te contactará en menos de 24 horas.',
              'For portfolios of 10+ villas. Our team will contact you within 24 hours.'
            )}
          </DialogDescription>
        </DialogHeader>

        {result?.type === 'success' ? (
          <div className="py-8 text-center space-y-4" data-testid="agency-demo-success">
            <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto" />
            <h3 className="text-xl font-semibold">
              {t('¡Solicitud recibida!', 'Request received!')}
            </h3>
            <p className="text-muted-foreground">{result.message}</p>
            <p className="text-sm text-muted-foreground">
              {t(
                'Revisa tu bandeja de entrada — enviamos una confirmación a',
                'Check your inbox — we sent a confirmation to'
              )}{' '}
              <strong>{formData.email}</strong>.
            </p>
            <Button onClick={resetAndClose} className="mt-4" data-testid="agency-demo-close-btn">
              {t('Cerrar', 'Close')}
            </Button>
          </div>
        ) : result?.type === 'redirect' ? (
          <div className="py-6 space-y-4" data-testid="agency-demo-redirect">
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-md">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">{result.message}</p>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={resetAndClose}>
                {t('Cancelar', 'Cancel')}
              </Button>
              <Button
                onClick={scrollToPricing}
                className="bg-gold-metallic hover:bg-gold-metallic/90 text-navy-deep"
                data-testid="agency-demo-view-plans-btn"
              >
                {t('Ver planes Self-Serve', 'View Self-Serve plans')}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="agency-demo-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="agency-name">{t('Nombre completo', 'Full Name')} *</Label>
                <Input
                  id="agency-name"
                  required
                  minLength={2}
                  value={formData.name}
                  onChange={handleChange('name')}
                  data-testid="agency-demo-name-input"
                />
              </div>
              <div>
                <Label htmlFor="agency-email">{t('Email corporativo', 'Business Email')} *</Label>
                <Input
                  id="agency-email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange('email')}
                  data-testid="agency-demo-email-input"
                />
              </div>
              <div>
                <Label htmlFor="agency-company">{t('Empresa', 'Company')}</Label>
                <Input
                  id="agency-company"
                  value={formData.company}
                  onChange={handleChange('company')}
                  data-testid="agency-demo-company-input"
                />
              </div>
              <div>
                <Label htmlFor="agency-phone">{t('Teléfono (WhatsApp)', 'Phone (WhatsApp)')}</Label>
                <Input
                  id="agency-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange('phone')}
                  data-testid="agency-demo-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="agency-props">
                  {t('Número de propiedades', 'Number of properties')} *
                </Label>
                <Input
                  id="agency-props"
                  type="number"
                  min={1}
                  max={10000}
                  required
                  value={formData.properties_count}
                  onChange={handleChange('properties_count')}
                  data-testid="agency-demo-properties-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t('Mínimo 10 para Agency', 'Minimum 10 for Agency')}
                </p>
              </div>
              <div>
                <Label htmlFor="agency-billing">
                  {t('Preferencia de facturación', 'Billing preference')}
                </Label>
                <select
                  id="agency-billing"
                  value={formData.billing_preference}
                  onChange={handleChange('billing_preference')}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="agency-demo-billing-select"
                >
                  <option value="monthly">{t('Mensual', 'Monthly')}</option>
                  <option value="annual">{t('Anual (15% desc.)', 'Annual (15% off)')}</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="agency-locations">
                {t('Ubicaciones (países/ciudades)', 'Locations (countries/cities)')}
              </Label>
              <Input
                id="agency-locations"
                placeholder={t('Ej: Costa Rica, Dubai, Tulum', 'e.g. Costa Rica, Dubai, Tulum')}
                value={formData.locations}
                onChange={handleChange('locations')}
                data-testid="agency-demo-locations-input"
              />
            </div>

            <div>
              <Label htmlFor="agency-message">{t('Mensaje (opcional)', 'Message (optional)')}</Label>
              <Textarea
                id="agency-message"
                rows={3}
                maxLength={2000}
                value={formData.message}
                onChange={handleChange('message')}
                placeholder={t(
                  'Cuéntanos sobre tu portafolio, retos actuales, o lo que esperas de la demo.',
                  'Tell us about your portfolio, current challenges, or what you expect from the demo.'
                )}
                data-testid="agency-demo-message-textarea"
              />
            </div>

            {result?.type === 'error' && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{result.message}</p>
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={resetAndClose}
                disabled={submitting}
              >
                {t('Cancelar', 'Cancel')}
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="agency-demo-submit-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t('Solicitar Demo', 'Request Demo')}
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground pt-2">
              {t(
                'Al enviar aceptas ser contactado por nuestro equipo comercial.',
                'By submitting you agree to be contacted by our sales team.'
              )}
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AgencyDemoModal;
